import { criterion, fail, fromCriteria } from "@/core/check";
import type { CheckResult, Lesson } from "@/core/types";
import { type DiagrammDetails, readDiagrammDetails } from "@/office/excel/analyse";
import { nichtPruefbar } from "../schnittstelle";

async function mitDiagrammen(bewerten: (diagramme: DiagrammDetails[]) => CheckResult): Promise<CheckResult> {
  const diagramme = await readDiagrammDetails();
  if (diagramme === null) {
    return nichtPruefbar(
      "Die Datenreihen und Achsen der Diagramme",
      "1.7",
      "Klicke das Diagramm an und prüfe die Einstellungen im Bereich „Diagrammelemente“.",
      true,
    );
  }
  if (diagramme.length === 0) {
    return fail("In der Arbeitsmappe gibt es noch kein Diagramm.", "Einfügen → Empfohlene Diagramme.");
  }
  return bewerten(diagramme);
}

export const lektionKombidiagramm: Lesson = {
  id: "kombidiagramm",
  title: "Kombidiagramm, Sekundärachse, Trendlinie",
  summary: "Zwei Größenordnungen in einem Diagramm – und die Richtung, in die die Werte laufen.",
  durationMinutes: 12,
  learningGoals: [
    "Ein Kombidiagramm mit Säulen und Linie erstellen",
    "Eine Datenreihe auf die Sekundärachse legen",
    "Trendlinie, Datenbeschriftungen und Achsentitel ergänzen",
  ],
  steps: [
    {
      id: "sekundaerachse",
      title: "Kombidiagramm mit Sekundärachse",
      explanation: [
        "Umsatz in Tausend Euro und Marge in Prozent passen nicht auf eine Achse: Die Marge " +
          "wäre ein flacher Strich am Boden. Ein Kombidiagramm zeigt beide – Umsatz als " +
          "Säulen, Marge als Linie auf einer zweiten Größenachse rechts.",
        "„Einfügen“ → „Kombidiagramm“ oder bei einem vorhandenen Diagramm „Diagrammtyp " +
          "ändern“ → „Kombi“: je Reihe den Typ wählen und bei der Marge „Sekundärachse“ " +
          "ankreuzen.",
        "Zwei Achsen verlangen Disziplin: Beide brauchen einen Achsentitel mit Einheit, " +
          "sonst liest niemand, welche Linie zu welcher Skala gehört.",
      ],
      keyPoints: [
        "Einfügen → Kombidiagramm.",
        "Unterschiedliche Größenordnungen → Sekundärachse.",
        "Beide Achsen mit Titel und Einheit.",
      ],
      task: {
        instruction:
          "Erstelle ein Kombidiagramm mit zwei Datenreihen, von denen eine als Linie auf der Sekundärachse liegt.",
        hint: "Daten markieren, Einfügen → Kombidiagramm → „Gruppierte Säulen – Linie auf Sekundärachse“.",
        check: () =>
          mitDiagrammen((diagramme) => {
            const kombi = diagramme.filter(
              (diagramm) =>
                diagramm.reihen.length >= 2 && new Set(diagramm.reihen.map((reihe) => reihe.chartType)).size >= 2,
            );
            const sekundaer = diagramme.filter((diagramm) =>
              diagramm.reihen.some((reihe) => reihe.axisGroup === "Secondary"),
            );
            const achseLesbar = diagramme.some((diagramm) => diagramm.reihen.some((reihe) => reihe.axisGroup !== null));
            return fromCriteria(
              [
                criterion("Ein Diagramm kombiniert zwei Diagrammarten", kombi.length > 0),
                ...(achseLesbar
                  ? [criterion("Eine Reihe liegt auf der Sekundärachse", sekundaer.length > 0)]
                  : []),
              ],
              {
                success: "Zwei Größenordnungen, ein lesbares Diagramm.",
                failure: "Das Kombidiagramm ist noch nicht vollständig.",
                hint: "Diagrammtyp ändern → Kombi → bei der zweiten Reihe „Linie“ und „Sekundärachse“.",
              },
            );
          }),
      },
    },
    {
      id: "trendlinie",
      title: "Trendlinie und Beschriftung",
      explanation: [
        "Eine Trendlinie zeigt die Richtung einer Reihe über alle Schwankungen hinweg: " +
          "Diagramm anklicken, Plus-Symbol „Diagrammelemente“ → „Trendlinie“. Linear passt " +
          "für gleichmäßiges Wachstum, „Gleitender Durchschnitt“ glättet Saisonschwankungen.",
        "Im selben Menü stehen „Datenbeschriftungen“ – die Werte direkt an den Säulen – und " +
          "„Achsentitel“. Beschriftungen an den Säulen ersetzen oft das Gitternetz: Wer den " +
          "Wert lesen kann, muss ihn nicht an der Achse abschätzen.",
      ],
      keyPoints: [
        "Diagrammelemente (+) → Trendlinie, Datenbeschriftungen, Achsentitel.",
        "Linear für Wachstum, gleitender Durchschnitt gegen Schwankungen.",
        "Datenbeschriftung statt Gitternetz.",
      ],
      task: {
        instruction:
          "Ergänze ein Diagramm um eine Trendlinie und um Datenbeschriftungen oder einen Titel der Größenachse.",
        hint: "Diagramm anklicken, + → Trendlinie, dann + → Datenbeschriftungen oder Achsentitel.",
        check: () =>
          mitDiagrammen((diagramme) => {
            const trend = diagramme.filter((diagramm) => diagramm.reihen.some((reihe) => reihe.trendlinien > 0));
            const beschriftet = diagramme.filter(
              (diagramm) =>
                diagramm.achsentitel.length > 0 || diagramm.reihen.some((reihe) => reihe.datenbeschriftung === true),
            );
            return fromCriteria(
              [
                criterion("Ein Diagramm hat eine Trendlinie", trend.length > 0),
                criterion("Ein Diagramm hat Datenbeschriftungen oder einen Achsentitel", beschriftet.length > 0),
              ],
              {
                success: "Die Richtung ist sichtbar und die Werte sind lesbar.",
                failure: "Trendlinie oder Beschriftung fehlen noch.",
              },
            );
          }),
      },
    },
  ],
};
